/**
 * /auto-subtitle-generator (SEO_BRIEF_04 item 14): three tools in a row, on one page.
 *
 *   1. TRANSCRIBE. A dropped video takes 7b (lib/video-flow.ts): its sound is copied out in the
 *      browser and offered through B6 (lib/upload-offer.ts), so only the audio is sent, with B6's
 *      limits, minutes and refusals. A video with subtitles inside is offered those instead. The
 *      page does not go to the job page: it polls the job itself and, when the transcript is
 *      ready, turns it into subtitle cues in the worker (lib/autosub.ts).
 *   2. EDIT. The cues open in the subtitle editor (lib/cue-editor.ts), with the video playing
 *      beside them from an object URL, never read or sent.
 *   3. SAVE OR BURN IN. Download in any format the editor writes, SRT first, or hand the edited
 *      subtitles and the video to the burn-in mounted further down this page
 *      (web/static/burn, by its `burn:video` and `burn:subtitles` events). The burn-in makes
 *      the new video in this tab.
 *
 * An audio file is transcribed the same way (nothing to burn into, until a video is added in the
 * editor); a subtitle file, dropped alone or with the video, opens in the editor straight away.
 *
 * Every word the page shows is in tools/autosub.html; this module holds codes only.
 */

import type { DocumentId, WriteFormat } from '../../../../../contracts/cue-model.ts';
import type { WritePayload } from '../../../../../src/core/protocol.ts';
import type { JobResponse } from '../lib/api-types.ts';
import { ApiError } from '../lib/api.ts';
import {
  baseName,
  burnFormat,
  cuesFromTranscript,
  handToBurn,
  subtitlesForBurn,
  type TranscriptWaitHandle,
  waitForTranscript,
} from '../lib/autosub.ts';
import { type CueEditorHandle, mountCueEditor } from '../lib/cue-editor.ts';
import { defaultSaveFormat } from '../lib/cue-editor-model.ts';
import { must, on, qs, setData } from '../lib/dom.ts';
import { saveBytes } from '../lib/download.ts';
import { classify, extensionOf, mountDropzone, videoTypeName } from '../lib/dropzone.ts';
import { track } from '../lib/events.ts';
import { clock } from '../lib/format.ts';
import { loadPlatforms } from '../lib/platforms.ts';
import { showProblem, showState, UPLOAD_FLAG, uploadVariant } from '../lib/states.ts';
import { within } from '../lib/upload.ts';
import { createUploadOffer } from '../lib/upload-offer.ts';
import { failedFormat, usageCounter } from '../lib/usage.ts';
import { createVideoFlow } from '../lib/video-flow.ts';
import { isVersionMismatch, WorkerFailure, worker } from '../lib/worker.ts';
import { downloadWriteOptions } from '../lib/write-options.ts';

interface AutosubConfig {
  readonly tool: { readonly slug: string; readonly label: string };
  readonly write_formats: readonly WriteFormat[];
  /** What a transcript saves as first: SRT (src/formats/video-tools.ts). */
  readonly transcript_format?: string | null;
  /**
   * A per-language subtitle generator's declared language (SEO_BRIEF_09 section 8,
   * src/formats/language-tools.ts), sent with the upload as the language pages send theirs.
   * Absent on /auto-subtitle-generator, which lets the model detect it.
   */
  readonly language?: string;
}

function readConfig(): AutosubConfig | null {
  const node = qs<HTMLScriptElement>('script[data-tool-config]');
  if (!node?.textContent) return null;
  try {
    const parsed = JSON.parse(node.textContent) as Partial<AutosubConfig>;
    if (!parsed.tool || !Array.isArray(parsed.write_formats)) return null;
    return parsed as AutosubConfig;
  } catch {
    return null;
  }
}

/** How long after the last edit the save is prepared, and the burn-in's copy refreshed. */
const SETTLE_MS = 600;

export function mountAutosubPage(): void {
  const page = qs<HTMLElement>('[data-autosub-page]');
  const found = readConfig();
  if (!page || !found) return;
  const config: AutosubConfig = found;

  const region = must<HTMLElement>('#banner-region');
  const dropHost = must<HTMLElement>('[data-dropzone-host]', page);
  const editorRoot = must<HTMLElement>('[data-cue-editor]', page);
  const progress = qs<HTMLElement>('[data-autosub-progress]', page);
  const statusLine = qs<HTMLElement>('[data-autosub-status]', page);
  const bar = qs<HTMLElement>('[data-autosub-bar]', page);
  const editHead = qs<HTMLElement>('[data-autosub-edit]', page);
  const formatField = qs<HTMLElement>('[data-write-format-field]', page);
  const formatSelect = qs<HTMLSelectElement>('[data-write-format-select]', page);
  const saveRow = qs<HTMLElement>('[data-autosub-save]', page);
  const downloadButton = qs<HTMLButtonElement>('[data-download]', page);
  const burnButton = qs<HTMLButtonElement>('[data-autosub-burn]', page);
  const burnNote = qs<HTMLElement>('[data-autosub-burn-note]', page);
  const burnRoot = qs<HTMLElement>('[data-burn-page]');
  const videoInput = qs<HTMLInputElement>('[data-editor-video-input]', editorRoot);
  const videoRemove = qs<HTMLButtonElement>('[data-editor-video-remove]', editorRoot);
  const downloadWord = downloadButton?.textContent?.trim() ?? '';

  const client = worker();
  const usage = usageCounter(config.tool.slug);

  /** The dropped (or added) video: the preview's and the burn-in's, never sent anywhere. */
  let video: File | null = null;
  /** The name the page's files are called after: the video's, or the audio's. */
  let sourceName = 'subtitles';
  let editor: CueEditorHandle | null = null;
  let opened: { readonly docId: DocumentId; readonly format: string | null } | null = null;
  let waiting: TranscriptWaitHandle | null = null;
  let generation = 0;
  let prepared: {
    readonly version: number;
    readonly format: WriteFormat;
    readonly payload: WritePayload;
  } | null = null;
  let settleTimer = 0;
  let burnOpen = false;

  function label(key: string, slots: Record<string, string> = {}): Node | null {
    const source = qs<HTMLElement>(`[data-autosub-label="${key}"]`, page ?? document);
    if (!source) return null;
    const node = source.cloneNode(true) as HTMLElement;
    node.removeAttribute('data-autosub-label');
    for (const [name, value] of Object.entries(slots)) {
      const slot = qs<HTMLElement>(`[data-slot="${name}"]`, node);
      if (slot) slot.textContent = value;
    }
    return node;
  }

  /* ── step 1: the wait for the transcript ───────────────────────────────── */

  function showProgress(
    key: string,
    slots: Record<string, string> = {},
    pct: number | null = null,
  ) {
    if (progress) progress.hidden = false;
    const line = label(key, slots);
    if (statusLine && line) statusLine.replaceChildren(line);
    if (bar) {
      if (pct === null) {
        setData(bar, 'indeterminate', 'true');
      } else {
        setData(bar, 'indeterminate', null);
        bar.style.setProperty('--progress-pct', `${pct.toFixed(1)}%`);
      }
    }
  }

  function hideProgress(): void {
    if (progress) progress.hidden = true;
  }

  function paintJob(job: JobResponse): void {
    if (job.status === 'queued') {
      showProgress('queued');
      return;
    }
    if (job.status !== 'running') return;
    const stage = job.stage ?? 'queued';
    const p = job.progress;
    const pct =
      p && p.total_s > 0 ? Math.max(0, Math.min(100, (p.done_s / p.total_s) * 100)) : null;
    showProgress(
      stage,
      p ? { done: clock(p.done_s), total: clock(p.total_s) } : {},
      stage === 'transcribing' ? pct : null,
    );
  }

  const uploads = createUploadOffer({
    region,
    tool: config.tool.slug,
    onSubmitted: (response) => {
      const mine = generation;
      region.replaceChildren();
      showProgress(response.resolved ? 'opening' : 'queued');
      waiting = waitForTranscript(response, (job) => {
        if (mine === generation) paintJob(job);
      });
      void waiting.done.then(async (outcome) => {
        if (mine !== generation) return;
        if (!outcome.ok) {
          hideProgress();
          // The job page's own failure wordings, in their upload form (E5_UPLOAD, E12_UPLOAD…).
          showState(region, uploadVariant(outcome.state), UPLOAD_FLAG);
          track('error_state', { state: outcome.state });
          return;
        }
        showProgress('opening');
        try {
          const cues = await cuesFromTranscript(client, outcome.publicId, sourceName);
          if (mine !== generation) {
            client.dispose([cues.docId], []);
            return;
          }
          openEditor(cues.docId, cues.cueCount, null);
          track('tool_used', { tool: config.tool.slug });
        } catch (err) {
          if (mine === generation) handleError(err);
        } finally {
          if (mine === generation) hideProgress();
        }
      });
    },
    ...(config.language ? { language: config.language } : {}),
  });

  const flow = createVideoFlow({
    client,
    region,
    mode: 'transcribe',
    status: (message) => drop.setLoaderStatus(message),
    offerUpload: (audio, banner, source) => void uploads.offer(audio, banner, source),
    uploadCap: () => within(loadPlatforms(), 4000, null).then((p) => p?.upload_max_bytes ?? null),
    onError: (err) => handleError(err),
    refuseVideo: (file) => refuseVideo(file),
  });

  /**
   * B5_VIDEO_FORMAT: a video in a format nothing here opens (lib/dropzone.ts), counted as
   * `video`. This page transcribes an audio file too, so the banner says so.
   */
  function refuseVideo(file: File): void {
    usage.failed(file, 'video');
    showState(region, 'B5_VIDEO_FORMAT', { format: videoTypeName(file.name), audio: 'y' });
  }

  function handleError(err: unknown): void {
    if (err instanceof ApiError) {
      showProblem(region, err.problem, {}, { upload: true });
      return;
    }
    if (isVersionMismatch(err)) {
      console.error('[autosub]', err);
      showState(region, 'STALE_PAGE', {});
      return;
    }
    if (err instanceof WorkerFailure) {
      const reason = err.reason;
      if (reason === 'empty' || reason === 'no-cues') {
        showState(region, 'B5_EMPTY', {});
        return;
      }
      if (reason === 'unsupported-binary' || reason === 'unknown-format') {
        showState(region, 'B5_UNSUPPORTED_BINARY', {});
        return;
      }
      const detail = err.detail as { line?: number; message?: string } | null;
      showState(region, 'B5_PARSE_FAILED', {
        format: '',
        line: detail?.line === undefined ? '' : String(detail.line),
        detail: detail?.message ?? reason,
      });
      return;
    }
    console.error('[autosub]', err);
    showState(region, 'NETWORK', {});
  }

  /* ── step 2: the editor ────────────────────────────────────────────────── */

  function closeEditor(): void {
    clearTimeout(settleTimer);
    prepared = null;
    editor?.destroy();
    editor = null;
    if (opened) client.dispose([opened.docId], []);
    opened = null;
    editorRoot.hidden = true;
    if (editHead) editHead.hidden = true;
    if (formatField) formatField.hidden = true;
    if (saveRow) saveRow.hidden = true;
    if (downloadButton) downloadButton.disabled = true;
    paintBurn();
  }

  function openEditor(docId: DocumentId, cueCount: number, format: string | null): void {
    closeEditor();
    opened = { docId, format };
    editor = mountCueEditor(editorRoot, {
      client,
      docId,
      cueCount,
      onChange: () => settle(),
      onError: (err) => handleError(err),
    });
    if (video) editor.setVideo(video);
    if (editHead) editHead.hidden = false;
    const own = format ?? config.transcript_format ?? 'srt';
    const save = formatSelect ? defaultSaveFormat(own, config.write_formats) : null;
    if (formatSelect && save) formatSelect.value = save;
    if (formatField) formatField.hidden = false;
    if (saveRow) saveRow.hidden = false;
    paintDownload();
    paintBurn();
    settle();
    (editHead ?? editorRoot).scrollIntoView?.({ block: 'start' });
  }

  /* ── step 3: save, or burn in ──────────────────────────────────────────── */

  function chosenFormat(): WriteFormat | null {
    const value = formatSelect?.value ?? config.write_formats[0];
    return config.write_formats.find((f) => f === value) ?? null;
  }

  function paintDownload(): void {
    if (!downloadButton) return;
    const format = chosenFormat();
    downloadButton.textContent = format ? `${downloadWord} ${format.toUpperCase()}` : downloadWord;
    downloadButton.disabled = editor === null || format === null;
  }

  function paintBurn(): void {
    if (burnButton) burnButton.disabled = editor === null || video === null;
    if (burnNote) burnNote.hidden = editor === null || video !== null;
    if (burnRoot && (editor === null || video === null)) {
      burnRoot.hidden = true;
      burnOpen = false;
    }
  }

  async function produce(format: WriteFormat): Promise<WritePayload | null> {
    if (!editor) return null;
    const current = editor;
    await current.flush();
    const version = current.state().version;
    const payload = await current.write(format, downloadWriteOptions(format));
    if (editor !== current) return null;
    if (current.state().version === version) prepared = { version, format, payload };
    return payload;
  }

  async function sendToBurn(): Promise<void> {
    if (!editor || !video || !burnRoot) return;
    const current = editor;
    await current.flush();
    const subtitles = await subtitlesForBurn(
      current,
      burnFormat(opened?.format ?? null),
      baseName(sourceName),
    );
    if (editor !== current || !video) return;
    handToBurn(burnRoot, video, subtitles);
  }

  /** After an edit: prepare the download, and keep the burn-in's copy current. */
  function settle(): void {
    clearTimeout(settleTimer);
    prepared = null;
    settleTimer = window.setTimeout(() => {
      const format = chosenFormat();
      if (format) void produce(format).catch(() => undefined);
      if (burnOpen) void sendToBurn().catch(handleError);
    }, SETTLE_MS);
  }

  function handOver(format: WriteFormat, payload: WritePayload): void {
    saveBytes(payload.output, payload.filename, format, 'utf-8');
    usage.downloaded(format, 'utf-8');
    track('format_download', { format });
  }

  if (downloadButton) {
    on(downloadButton, 'click', () => {
      const format = chosenFormat();
      if (!editor || !format) return;
      const fresh = prepared;
      if (fresh && fresh.format === format && fresh.version === editor.state().version) {
        // Synchronous from the click to the save: the bytes were made while the page was idle.
        handOver(format, fresh.payload);
        return;
      }
      region.replaceChildren();
      void produce(format)
        .then((payload) => {
          if (payload) handOver(format, payload);
        })
        .catch(handleError);
    });
  }

  if (formatSelect) {
    on(formatSelect, 'change', () => {
      paintDownload();
      settle();
    });
  }

  if (burnButton) {
    on(burnButton, 'click', () => {
      if (!editor || !video || !burnRoot) return;
      burnRoot.hidden = false;
      burnOpen = true;
      void sendToBurn()
        .then(() => burnRoot.scrollIntoView?.({ block: 'start' }))
        .catch(handleError);
      track('tool_used', { tool: 'burn-subtitles' });
    });
  }

  // A video added in the editor's own toolbar is the burn-in's video too; removed, it is not.
  if (videoInput) {
    on(videoInput, 'change', () => {
      const file = videoInput.files?.[0];
      if (file && classify(file) === 'container') {
        video = file;
        paintBurn();
        if (burnOpen) void sendToBurn().catch(handleError);
      }
    });
  }
  if (videoRemove) {
    on(videoRemove, 'click', () => {
      video = null;
      paintBurn();
    });
  }

  /* ── the drop ──────────────────────────────────────────────────────────── */

  /** A newer drop supersedes everything the last one started. */
  function reset(): void {
    generation += 1;
    waiting?.stop();
    waiting = null;
    uploads.supersede();
    flow.abandon();
    hideProgress();
    region.replaceChildren();
    closeEditor();
  }

  async function openSubtitleFile(file: File, mine: number): Promise<void> {
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const info = await client.request('parse', {
        inputName: file.name,
        bytes,
        retainRaw: false,
        retainBytes: false,
      });
      if (mine !== generation) {
        client.dispose([info.docId], [info.inputId]);
        return;
      }
      usage.loaded(file, info.format);
      openEditor(info.docId, info.cueCount, info.format);
    } catch (err) {
      if (mine !== generation) return;
      usage.failed(file, failedFormat(extensionOf(file.name), err));
      handleError(err);
    }
  }

  function take(files: readonly File[]): void {
    const container = files.find((f) => classify(f) === 'container') ?? null;
    const subtitle = files.find((f) => classify(f) === 'subtitle') ?? null;
    const audio = files.find((f) => classify(f) === 'audio') ?? null;
    const first = files[0];
    if (!first) return;
    reset();
    const mine = generation;
    video = container;
    sourceName = (container ?? subtitle ?? audio ?? first).name;

    if (subtitle) {
      // Subtitles already made: straight to the editor, with the video beside them if it came too.
      void openSubtitleFile(subtitle, mine);
      return;
    }
    if (container) {
      void flow.ingest(container, {
        retainBytes: false,
        stale: () => mine !== generation,
        // The video's own subtitles, when the visitor chose them over transcribing.
        land: async (info) => {
          if (mine !== generation) return;
          openEditor(info.docId, info.cueCount, info.format);
        },
      });
      return;
    }
    if (audio) {
      void uploads.offer(audio);
      return;
    }
    const refused = files.find((f) => classify(f) === 'other-video');
    if (refused) {
      refuseVideo(refused);
      return;
    }
    const kind = classify(first);
    usage.failed(first, failedFormat(extensionOf(first.name), null));
    showState(region, kind === 'archive' ? 'B5_ARCHIVE_HERE' : 'B5_UNSUPPORTED_BINARY', {});
  }

  const drop = mountDropzone(dropHost, { onFiles: (files) => take(files) });

  on(region, 'click', (ev) => {
    const target = ev.target;
    if (!(target instanceof HTMLElement)) return;
    const action = target.getAttribute('data-action');
    if (action === 'reload') location.reload();
    // "Try again" in a failure banner: the video is still here, so the visitor drops it again
    // from this page rather than being sent to the homepage as the job page does.
    if (action === 'retry') region.replaceChildren();
  });

  addEventListener('pagehide', () => {
    waiting?.stop();
    closeEditor();
    drop.destroy();
  });
}
